import {ChangeEvent, useContext, useEffect, useState} from "react";
import {Button, FormControl, IconButton, InputAdornment, InputLabel, Link, OutlinedInput, Paper, Stack, TextField, Typography}
  from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import useSession from "../hooks/user/useSession";
import {MessageContext} from "../context/messageContext";
import {PulseLoader} from "react-spinners";
import {useResponsive} from "../hooks/style";
import {termsOfServiceText} from "./termsOfServiceText";
import {privacyPolicyText} from "./privacyPolicyText";
import InfoDialog from "../components/InfoDialog";

const testUsername = "testuser";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useSession();
  const {
    messageState: [, setMessage],
  } = useContext(MessageContext);
  const {isDesktop, isPhone} = useResponsive();

  const emailRegex = new RegExp(
    /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [, setValidationCode] = useState("");
  const [inputsValid, setInputsValid] = useState(false);
  const [isLoggingIn, setLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [termsOfServiceOpen, setTermsOfServiceOpen] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);

  const validateInputs = () => {
    const validEmail = emailRegex.test(email) || email === testUsername;
    const validPassword = !!password.length;
    const inputsValid = validEmail && validPassword;
    setInputsValid(inputsValid);
  };

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value },
    } = event;
    switch (name) {
      case "email":
        setEmail(value);
        break;
      case "password":
        setPassword(value);
        break;
      case "validationCode":
        setValidationCode(value);
        break;
      default:
        setMessage({ type: "error", message: `Cannot handle change event.` });
        console.error(`Cannot handle change event.`);
        break;
    }
  };

  const handleLogin = () => {
    setLoggingIn(true);
    login(
      email,
      password,
      () => navigate("/"),
      (err) => {
        setLoggingIn(false);
        setMessage({ type: "error", message: err?.message || 'An unknown error occurred attempting to log in.' })
      }
    );
  };

  useEffect(validateInputs, [email, password]);

  return (
    <Paper sx={{ px: isPhone ? 2 : 0, py: 6, mx: isPhone ? 2 : "auto", maxWidth: "sm" }}>
      <Stack
        direction="column"
        spacing={4}
        sx={{mx: 'auto', maxWidth: 300, alignItems: 'center'}}
      >
        <Typography variant="h4">Login</Typography>

        <TextField
          variant="outlined"
          fullWidth
          error={!(emailRegex.test(email) || email === testUsername) && !!email}
          type="email"
          label="Email"
          name="email"
          value={email}
          onChange={handleTextChange}
          onKeyDown={event => {
            if (event.key === 'Enter')
              if (inputsValid && !isLoggingIn)
                handleLogin();
          }}
        />

        <FormControl variant="outlined" fullWidth>
          <InputLabel>Password</InputLabel>
          <OutlinedInput
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={handleTextChange}
            onKeyDown={event => {
              if (event.key === 'Enter')
                if (inputsValid && !isLoggingIn)
                  handleLogin();
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(show => !show)}
                  onMouseDown={event => event.preventDefault()}
                  onMouseUp={event => event.preventDefault()}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff/> : <Visibility/>}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>

        <Stack
          direction="row"
          sx={{mt: 2, width: '100%', justifyContent: 'space-between'}}
        >
          <Stack direction="column" spacing={2}>
            <Link
              component={RouterLink}
              variant="body1"
              to="/forgot-password"
            >
              Forgot password?
            </Link>

            <Link
              component="button"
              variant="body1"
              color="textSecondary"
              align="left"
              onClick={() => setTermsOfServiceOpen(true)}
            >
              Terms of Service
            </Link>

            <Link
              component="button"
              variant="body1"
              color="textSecondary"
              align="left"
              onClick={() => setPrivacyPolicyOpen(true)}
            >
              Privacy Policy
            </Link>
          </Stack>

          <Button
            variant="contained"
            color="primary"
            disabled={!inputsValid || isLoggingIn}
            onClick={handleLogin}
            sx={{height: 'max-content'}}
          >
            { isLoggingIn ? (
              <PulseLoader color='white' />
            ) : 'Login'}
          </Button>
        </Stack>
      </Stack>

      <InfoDialog
        open={termsOfServiceOpen}
        handleClose={() => setTermsOfServiceOpen(false)}
        fullScreen={isPhone}
        title='Terms of Service'
        content={termsOfServiceText}
      />

      <InfoDialog
        open={privacyPolicyOpen}
        handleClose={() => setPrivacyPolicyOpen(false)}
        fullScreen={isPhone}
        title='Privacy Policy'
        content={privacyPolicyText}
      />
    </Paper>
  );
}
